import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, Globe, Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const SIZE_OPTIONS = [
  { value: "1-10", label: "1–10 (Solo / Tiny team)" },
  { value: "11-50", label: "11–50 (Small business)" },
  { value: "51-200", label: "51–200 (Mid-market)" },
  { value: "201-1000", label: "201–1,000 (Mid-large)" },
  { value: "1000+", label: "1,000+ (Enterprise)" },
];

const SUGGESTED_INDUSTRIES = [
  "B2B SaaS",
  "Fintech",
  "Healthcare",
  "Manufacturing",
  "Retail / E-commerce",
  "Education",
  "Logistics",
  "Real Estate",
  "Marketing / Adtech",
  "Cybersecurity",
];

export default function ProfileSettings() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tierAllows, setTierAllows] = useState(false);
  const [step, setStep] = useState(1); // 1=URL, 2=review+wizard, 3=saved
  const [working, setWorking] = useState(false);

  // form state
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [description, setDescription] = useState("");
  const [whatWeSell, setWhatWeSell] = useState("");
  const [valueProps, setValueProps] = useState([]);
  const [targetIndustries, setTargetIndustries] = useState([]);
  const [customIndustryInput, setCustomIndustryInput] = useState("");
  const [targetSizes, setTargetSizes] = useState([]);
  const [topPain, setTopPain] = useState("");

  // load existing profile (if any)
  useEffect(() => {
    let cancelled = false;
    http
      .get("/profile")
      .then(({ data }) => {
        if (cancelled) return;
        setTierAllows(!!data.tier_allows_profile);
        if (data.profile) {
          // pre-fill all fields and skip to review step
          const p = data.profile;
          setWebsiteUrl(p.website_url || "");
          setCompanyName(p.company_name || "");
          setDescription(p.description || "");
          setWhatWeSell(p.what_we_sell || "");
          setValueProps(p.value_props || []);
          setTargetIndustries(p.target_industries || []);
          setTargetSizes(
            (p.target_company_sizes && p.target_company_sizes.length > 0)
              ? p.target_company_sizes
              : (p.target_company_size ? [p.target_company_size] : [])
          );
          setTopPain(p.top_pain_solved || "");
          setStep(2);
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const onAnalyze = async () => {
    if (!websiteUrl.trim()) {
      toast.error("Enter your company website URL first");
      return;
    }
    const isStep2 = step === 2;
    setWorking(true);
    try {
      const { data } = await http.post("/profile/preview", { website_url: websiteUrl.trim() });
      const p = data.preview || {};
      setCompanyName(p.company_name || "");
      setDescription(p.description || "");
      setWhatWeSell(p.what_we_sell || "");
      setValueProps(p.value_props || []);
      // On step 2 (re-analyze), preserve the user's wizard answers instead of overwriting.
      if (!isStep2) {
        setTargetIndustries(p.suggested_target_industries || []);
        setTargetSizes(
          p.suggested_company_size ? [p.suggested_company_size] : []
        );
        setTopPain(p.suggested_top_pain || "");
        setStep(2);
        toast.success("Website analyzed — review & confirm below");
      } else {
        toast.success("Website re-analyzed — fields refreshed");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not analyze that URL");
    } finally {
      setWorking(false);
    }
  };

  const onSave = async () => {
    if (!companyName.trim() || !description.trim() || !whatWeSell.trim()) {
      toast.error("Company name, description and what-you-sell are required");
      return;
    }
    setWorking(true);
    try {
      await http.post("/profile", {
        company_name: companyName.trim(),
        website_url: websiteUrl.trim(),
        description: description.trim(),
        what_we_sell: whatWeSell.trim(),
        value_props: valueProps,
        target_industries: targetIndustries,
        target_company_sizes: targetSizes,
        top_pain_solved: topPain.trim(),
      });
      toast.success("Profile saved · fit scoring active");
      setStep(3);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setWorking(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete your company profile? Future enrichments will fall back to the generic opportunity score.")) return;
    setWorking(true);
    try {
      await http.delete("/profile");
      setStep(1);
      setWebsiteUrl("");
      setCompanyName("");
      setDescription("");
      setWhatWeSell("");
      setValueProps([]);
      setTargetIndustries([]);
      setCustomIndustryInput("");
      setTargetSizes([]);
      setTopPain("");
      toast.success("Profile cleared");
    } catch {
      toast.error("Could not delete profile");
    } finally {
      setWorking(false);
    }
  };

  const toggleIndustry = (ind) => {
    setTargetIndustries((prev) =>
      prev.includes(ind) ? prev.filter((i) => i !== ind) : [...prev, ind]
    );
  };

  const addCustomIndustry = () => {
    const v = customIndustryInput.trim();
    if (!v) return;
    setTargetIndustries((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setCustomIndustryInput("");
  };

  const toggleSize = (val) => {
    setTargetSizes((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const updateValueProp = (i, val) => {
    setValueProps((prev) => prev.map((v, idx) => (idx === i ? val : v)));
  };

  const addValueProp = () => setValueProps((prev) => [...prev, ""]);
  const removeValueProp = (i) =>
    setValueProps((prev) => prev.filter((_, idx) => idx !== i));

  const allCustomIndustries = useMemo(() => {
    const merged = [...SUGGESTED_INDUSTRIES];
    targetIndustries.forEach((ind) => {
      if (!merged.includes(ind)) merged.push(ind);
    });
    return merged;
  }, [targetIndustries]);

  if (loading) {
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-ink-400" />
      </div>
    );
  }

  // -------- Tier-locked view --------
  if (!tierAllows) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/dashboard"
          className="text-[10px] uppercase tracking-widestx font-semibold text-ink-400 hover:text-ink-900 inline-flex items-center gap-1 mb-6"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to dashboard
        </Link>
        <div
          data-testid="profile-locked"
          className="bg-white border border-ink-100 rounded-sm p-10 text-center"
        >
          <div className="inline-grid place-items-center w-12 h-12 rounded-sm bg-ink-100 mb-4">
            <Lock className="w-5 h-5 text-ink-700" />
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            AI fit scoring is a Growth+ feature.
          </h1>
          <p className="text-sm text-ink-500 max-w-lg mx-auto mt-3">
            Tell LeadIQ what your company sells and we'll score every prospect against{" "}
            <span className="font-mono text-ink-900">your</span> ICP — not just generic
            interest. Stop chasing wrong-fit leads.
          </p>
          <Button
            data-testid="profile-locked-upgrade"
            onClick={() => nav("/pricing")}
            className="mt-6 rounded-sm bg-ink-900 hover:bg-ink-700 text-white"
          >
            See plans
          </Button>
        </div>
      </div>
    );
  }

  // -------- Wizard --------
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="profile-page">
      <Link
        to="/dashboard"
        className="text-[10px] uppercase tracking-widestx font-semibold text-ink-400 hover:text-ink-900 inline-flex items-center gap-1 mb-6"
      >
        <ArrowLeft className="w-3 h-3" />
        Back to dashboard
      </Link>

      <div className="mb-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-mono text-ink-400">
        <Sparkles className="w-3 h-3" />
        AI Fit Scoring · Setup
      </div>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-ink-900">
        Tell LeadIQ what you sell.
      </h1>
      <p className="text-sm text-ink-500 mt-3 max-w-2xl">
        We'll learn your ICP and score every prospect for fit — not just generic
        interest. Takes ~2 minutes.
      </p>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mt-8 mb-6">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              data-testid={`profile-step-indicator-${s}`}
              className={`grid place-items-center w-7 h-7 rounded-sm text-[10px] font-mono font-semibold transition-colors ${
                step >= s ? "bg-ink-900 text-white" : "bg-ink-100 text-ink-400"
              }`}
            >
              {step > s ? <Check className="w-3 h-3" /> : s}
            </div>
            {s < 3 && (
              <div
                className={`w-12 h-0.5 ${step > s ? "bg-ink-900" : "bg-ink-100"}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step 1 — URL */}
      {step === 1 && (
        <div
          data-testid="profile-step-1"
          className="bg-white border border-ink-100 rounded-sm p-6 sm:p-8"
        >
          <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 block mb-2">
            Your company website
          </label>
          <div className="relative">
            <Globe className="w-4 h-4 text-ink-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="profile-website-input"
              type="text"
              placeholder="https://yourcompany.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="w-full pl-10 pr-3 h-12 rounded-sm border border-ink-200 bg-white text-base text-ink-900 placeholder:text-ink-300 focus:outline-none focus:border-ink-700 transition-colors"
            />
          </div>
          <p className="text-xs text-ink-500 mt-3">
            We'll scrape your homepage + about page and extract a starting ICP. You can
            edit everything in the next step.
          </p>
          <div className="mt-6 flex gap-3">
            <Button
              data-testid="profile-analyze-btn"
              onClick={onAnalyze}
              disabled={working}
              className="rounded-sm bg-ink-900 hover:bg-ink-700 text-white"
            >
              {working ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing site…
                </>
              ) : (
                <>
                  Analyze my website
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
            <Button
              data-testid="profile-skip-btn"
              variant="ghost"
              onClick={() => setStep(2)}
              className="rounded-sm"
            >
              Skip — I'll fill it in manually
            </Button>
          </div>
        </div>
      )}

      {/* Step 2 — review + wizard */}
      {step === 2 && (
        <div
          data-testid="profile-step-2"
          className="bg-white border border-ink-100 rounded-sm p-6 sm:p-8 space-y-6"
        >
          <div>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                Your company website
              </label>
              <span className="text-[10px] text-ink-400">
                Edit and re-analyze to refresh extracted info
              </span>
            </div>
            <div className="flex gap-2 flex-wrap sm:flex-nowrap">
              <div className="relative flex-1 min-w-[220px]">
                <Globe className="w-4 h-4 text-ink-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="profile-website-step2"
                  type="text"
                  placeholder="https://yourcompany.com"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  className="w-full pl-10 pr-3 h-10 rounded-sm border border-ink-200 bg-white text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:border-ink-700 transition-colors"
                />
              </div>
              <Button
                data-testid="profile-reanalyze-btn"
                variant="outline"
                onClick={onAnalyze}
                disabled={working || !websiteUrl.trim()}
                className="rounded-sm shrink-0 border-ink-200 text-ink-700 hover:bg-ink-100"
              >
                {working ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    Re-analyze
                  </>
                )}
              </Button>
            </div>
            <p className="text-[11px] text-ink-400 mt-2">
              Adding/changing the URL re-runs the AI extraction and overwrites the fields
              below — your wizard answers (industries, size, pain) are kept.
            </p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 block mb-2">
              Company name
            </label>
            <input
              data-testid="profile-company-name"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 h-10 rounded-sm border border-ink-200 bg-white text-sm text-ink-900 focus:outline-none focus:border-ink-700"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 block mb-2">
              One-line description
            </label>
            <textarea
              data-testid="profile-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-sm border border-ink-200 bg-white text-sm text-ink-900 focus:outline-none focus:border-ink-700"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 block mb-2">
              What you sell
            </label>
            <textarea
              data-testid="profile-what-we-sell"
              value={whatWeSell}
              onChange={(e) => setWhatWeSell(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-sm border border-ink-200 bg-white text-sm text-ink-900 focus:outline-none focus:border-ink-700"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                Value props (3–5)
              </label>
              <button
                data-testid="profile-add-value-prop"
                type="button"
                onClick={addValueProp}
                className="text-[10px] uppercase tracking-widestx font-semibold text-ink-700 hover:text-ink-900"
              >
                + add
              </button>
            </div>
            <div className="space-y-2">
              {valueProps.map((vp, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    data-testid={`profile-value-prop-${i}`}
                    type="text"
                    value={vp}
                    onChange={(e) => updateValueProp(i, e.target.value)}
                    className="flex-1 px-3 h-9 rounded-sm border border-ink-200 bg-white text-sm focus:outline-none focus:border-ink-700"
                  />
                  <button
                    type="button"
                    onClick={() => removeValueProp(i)}
                    className="text-ink-400 hover:text-accent p-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* 3-question wizard */}
          <div className="border-t border-ink-100 pt-6">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-4">
              ICP wizard · 3 quick questions
            </div>

            {/* Q1 */}
            <div className="mb-5">
              <label className="text-sm font-semibold text-ink-900 block mb-2">
                1 · Which industries do you target?
              </label>
              <div className="flex flex-wrap gap-2">
                {allCustomIndustries.map((ind) => {
                  const active = targetIndustries.includes(ind);
                  return (
                    <button
                      key={ind}
                      data-testid={`profile-industry-${ind.replace(/[^a-z0-9]+/gi, "-")}`}
                      type="button"
                      onClick={() => toggleIndustry(ind)}
                      className={`text-[11px] uppercase tracking-widestx font-semibold px-3 py-1.5 rounded-sm border transition-colors ${
                        active
                          ? "bg-ink-900 text-white border-ink-900"
                          : "bg-white text-ink-700 border-ink-200 hover:border-ink-700"
                      }`}
                    >
                      {ind}
                    </button>
                  );
                })}
              </div>
              {/* Other / custom industry */}
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="text"
                  data-testid="profile-industry-other-input"
                  value={customIndustryInput}
                  onChange={(e) => setCustomIndustryInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomIndustry();
                    }
                  }}
                  placeholder="Other — type an industry and press Enter"
                  className="flex-1 px-3 py-2 rounded-sm border border-ink-200 bg-white text-sm focus:outline-none focus:border-ink-700"
                />
                <Button
                  type="button"
                  data-testid="profile-industry-other-add"
                  onClick={addCustomIndustry}
                  disabled={!customIndustryInput.trim()}
                  className="rounded-sm bg-ink-900 hover:bg-ink-700 text-white text-xs h-9 px-3"
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Q2 */}
            <div className="mb-5">
              <label className="text-sm font-semibold text-ink-900 block mb-2">
                2 · Typical company size you sell to?
                <span className="ml-2 text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                  · select all that apply
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {SIZE_OPTIONS.map((opt) => {
                  const active = targetSizes.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      data-testid={`profile-size-${opt.value}`}
                      type="button"
                      onClick={() => toggleSize(opt.value)}
                      className={`text-xs px-3 py-2 rounded-sm border text-left transition-colors ${
                        active
                          ? "bg-ink-900 text-white border-ink-900"
                          : "bg-white text-ink-700 border-ink-200 hover:border-ink-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Q3 */}
            <div>
              <label className="text-sm font-semibold text-ink-900 block mb-2">
                3 · Top pain you solve?
              </label>
              <textarea
                data-testid="profile-top-pain"
                value={topPain}
                onChange={(e) => setTopPain(e.target.value)}
                placeholder="e.g. Marketing teams can't send personalized email at scale without engineering help."
                rows={3}
                className="w-full px-3 py-2 rounded-sm border border-ink-200 bg-white text-sm focus:outline-none focus:border-ink-700"
              />
            </div>
          </div>

          <div className="flex justify-between gap-3 pt-2">
            <Button
              data-testid="profile-back-btn"
              variant="ghost"
              onClick={() => setStep(1)}
              className="rounded-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              data-testid="profile-save-btn"
              onClick={onSave}
              disabled={working}
              className="rounded-sm bg-ink-900 hover:bg-ink-700 text-white"
            >
              {working ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  Save profile
                  <Check className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3 — saved confirmation */}
      {step === 3 && (
        <div
          data-testid="profile-step-3"
          className="bg-white border border-ink-100 rounded-sm p-10 text-center"
        >
          <div className="inline-grid place-items-center w-12 h-12 rounded-sm bg-[#E8F5E9] mb-4">
            <Check className="w-6 h-6 text-[#2E7D32]" />
          </div>
          <h2 className="font-display font-extrabold text-3xl tracking-tight">
            Fit scoring is live.
          </h2>
          <p className="text-sm text-ink-500 max-w-md mx-auto mt-3">
            Every new lead you enrich will now show both an{" "}
            <span className="font-mono text-ink-900">opportunity score</span> and a{" "}
            <span className="font-mono text-ink-900">fit score</span> personalized to{" "}
            {companyName || "your company"}.
          </p>
          <p className="text-xs text-ink-400 mt-2">
            Re-enrich existing leads to apply fit scoring to them (1 credit each).
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button
              data-testid="profile-edit-btn"
              variant="ghost"
              onClick={() => setStep(2)}
              className="rounded-sm"
            >
              Edit profile
            </Button>
            <Button
              data-testid="profile-done-btn"
              onClick={() => nav("/dashboard")}
              className="rounded-sm bg-ink-900 hover:bg-ink-700 text-white"
            >
              Back to dashboard
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <button
            data-testid="profile-delete-btn"
            onClick={onDelete}
            className="text-[10px] uppercase tracking-widestx font-semibold text-ink-400 hover:text-accent mt-8"
          >
            Delete profile
          </button>
        </div>
      )}
    </div>
  );
}
